import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { DEAL_STAGES, type CrmDeal, type DealStage } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n';
import { formatDate } from '../../lib/format';
import { Loading, PageHeader } from '../../components/ui';
import { crmLabel } from '../../lib/crm';
import { useMoveDeal } from './StageMove';

/** `paying` is shown but never a drop target: it follows the first paid invoice. */
const MANUAL_TARGETS = DEAL_STAGES.filter((s) => s !== 'paying');

export function CrmPipeline() {
  const { company } = useAuth();
  const { t, lang } = useI18n();
  const deals = useQuery({ queryKey: ['crm-deals', company?.id, 'all'], queryFn: () => capi<CrmDeal[]>('/crm/deals?limit=500') });
  const { move, dialog } = useMoveDeal();
  const [over, setOver] = useState<DealStage | null>(null);
  const [dragging, setDragging] = useState<CrmDeal | null>(null);

  return (
    <>
      <PageHeader crumb={t('crmCrumb')} title={t('pipeTitle')} sub={t('pipeSub')} />
      {!deals.data ? <Loading error={deals.error} /> : (
        <div className="kanban" role="list">
          {DEAL_STAGES.map((stage) => {
            const inStage = deals.data.filter((d) => d.stage === stage);
            const droppable = stage !== 'paying' && dragging && dragging.stage !== stage;
            return (
              <section
                key={stage} role="listitem" aria-label={crmLabel('stage', stage, lang)}
                className={`col ${over === stage && droppable ? 'over' : ''}`}
                onDragOver={(e) => { if (droppable) { e.preventDefault(); setOver(stage); } }}
                onDragLeave={() => setOver((o) => (o === stage ? null : o))}
                onDrop={(e) => { e.preventDefault(); setOver(null); if (dragging && droppable) move(dragging, stage); setDragging(null); }}
              >
                <div className="colhead"><span>{crmLabel('stage', stage, lang)}</span><span className="chip neutral">{inStage.length}</span></div>
                {inStage.map((d) => (
                  <article
                    key={d.id} className="subcard dealcard" draggable={d.stage !== 'paying' && d.stage !== 'lost'}
                    onDragStart={(e) => { setDragging(d); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => { setDragging(null); setOver(null); }}
                  >
                    <Link to={`/crm/accounts/${d.accountId}`} style={{ fontWeight: 700, fontSize: '0.84rem', color: 'inherit' }}>{d.accountName}</Link>
                    <span className="small secondary">{d.title}</span>
                    <span className="small muted">
                      {d.ownerName ?? t('noOwner')}
                      {d.demoAt && stage === 'demo_scheduled' ? ` · ${formatDate(d.demoAt, lang, true)}` : ''}
                      {d.lostReason ? ` · ${crmLabel('lostReason', d.lostReason, lang)}` : ''}
                    </span>
                    {d.stage !== 'paying' && d.stage !== 'lost' && (
                      // The keyboard and touch way to move a card.
                      <select aria-label={`${t('moveTo')} ${d.title}`} value="" onChange={(e) => { if (e.target.value) move(d, e.target.value as DealStage); }}>
                        <option value="">{t('moveTo')}</option>
                        {MANUAL_TARGETS.filter((s) => s === 'lost' || DEAL_STAGES.indexOf(s) > DEAL_STAGES.indexOf(d.stage)).map((s) => (
                          <option key={s} value={s}>{crmLabel('stage', s, lang)}</option>
                        ))}
                      </select>
                    )}
                  </article>
                ))}
              </section>
            );
          })}
        </div>
      )}
      {dialog}
    </>
  );
}
